// Porta JS do módulo do back-end (`src/project/disbursement/calendar.js`). Aritmética de
// calendário determinística para a defasagem em dias: conversão civil <-> dia serial,
// calendário de feriados nacionais (fixos e derivados da Páscoa) e contagem de dias
// úteis. Sem `Date`, sem fuso, sem I/O — pura aritmética inteira, para que o motor do
// editor produza a mesma competência de destino que a rota vai gravar.
//
// Dia serial 0 é 1970-01-01 (uma quinta-feira). A conversão civil <-> serial é o
// algoritmo de Howard Hinnant (days_from_civil / civil_from_days,
// http://howardhinnant.github.io/date_algorithms.html) para o calendário gregoriano
// proléptico, válido para qualquer ano que caiba num inteiro seguro.
import { monthKey, parseMonthKey } from "./hierarchy";

export const isLeapYear = (year) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const DAYS_IN_MONTH = Object.freeze([31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]);

export const daysInMonth = (year, month) => (
  month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1]
);

/** Civil (ano, mês 1-12, dia) -> dias desde 1970-01-01. */
export const civilToSerial = (year, month, day) => {
  const y = month <= 2 ? year - 1 : year;
  const era = Math.floor((y >= 0 ? y : y - 399) / 400);
  const yoe = y - era * 400;
  const mp = (month + 9) % 12;
  const doy = Math.floor((153 * mp + 2) / 5) + day - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
};

/** Inverso de civilToSerial: dias desde 1970-01-01 -> { year, month, day }. */
export const serialToCivil = (serial) => {
  const z = serial + 719468;
  const era = Math.floor((z >= 0 ? z : z - 146096) / 146097);
  const doe = z - era * 146097;
  const yoe = Math.floor(
    (doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365,
  );
  const y = yoe + era * 400;
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const day = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const month = mp < 10 ? mp + 3 : mp - 9;
  const year = month <= 2 ? y + 1 : y;
  return { year, month, day };
};

/** Competência ("YYYY-MM") de `serial`, via a mesma autoridade de forma/limites de hierarchy.js. */
export const monthKeyOfSerial = (serial) => {
  const { year, month } = serialToCivil(serial);
  return monthKey(year, month);
};

// 1970-01-01 (serial 0) é quinta-feira. `((serial % 7) + 11) % 7` continua correto para
// `serial` negativo: o `%` do JS preserva o sinal do dividendo, mas somar 11 (um múltiplo
// de 7 mais o deslocamento de quinta-feira = 4) antes do `% 7` final traz o resultado de
// volta para [0, 6] sem um segundo caso. 0 = domingo ... 6 = sábado.
const dayOfWeek = (serial) => ((serial % 7) + 11) % 7;

const isWeekend = (serial) => {
  const dow = dayOfWeek(serial);
  return dow === 0 || dow === 6;
};

/**
 * Domingo de Páscoa de `year`, como { month, day } (civil, gregoriano). Algoritmo
 * gregoriano anônimo (Meeus/Jones/Butcher) — aritmética inteira, válido para qualquer ano.
 */
export const easterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const monthDay = h + l - 7 * m + 114;
  const month = Math.floor(monthDay / 31);
  const day = (monthDay % 31) + 1;
  return { month, day };
};

// Feriados nacionais de data fixa (design D5): [mês, dia].
const FIXED_HOLIDAYS = Object.freeze([
  [1, 1], // Confraternização Universal
  [4, 21], // Tiradentes
  [5, 1], // Dia do Trabalho
  [9, 7], // Independência do Brasil
  [10, 12], // Nossa Senhora Aparecida
  [11, 2], // Finados
  [11, 15], // Proclamação da República
  [11, 20], // Consciência Negra
  [12, 25], // Natal
]);

// Feriados móveis observados para prazo de pagamento (design D5), como deslocamento em
// dias a partir do Domingo de Páscoa. Carnaval e Corpus Christi não são feriados
// nacionais estatutários, mas o setor os trata como não úteis para pagamento; ficam aqui,
// num lugar só, comentados e auditáveis, ao lado das oito datas estatutárias acima.
const EASTER_OFFSETS = Object.freeze([
  -48, // Carnaval (segunda-feira)
  -47, // Carnaval (terça-feira)
  -2, // Sexta-feira Santa
  60, // Corpus Christi
]);

const holidaySerialsOfYear = (year) => {
  const easter = easterSunday(year);
  const easterSerial = civilToSerial(year, easter.month, easter.day);
  const fixed = FIXED_HOLIDAYS.map(([month, day]) => civilToSerial(year, month, day));
  const movable = EASTER_OFFSETS.map((offset) => easterSerial + offset);
  return [...fixed, ...movable];
};

const holidaySetCache = new Map();
const holidaySetOfYear = (year) => {
  let set = holidaySetCache.get(year);
  if (!set) {
    set = new Set(holidaySerialsOfYear(year));
    holidaySetCache.set(year, set);
  }
  return set;
};

// Todo feriado acima cai dentro do ano civil que o seu próprio nome designa (os móveis
// também: o mais cedo, Carnaval segunda, nunca é antes do início de fevereiro), então um
// feriado sempre é encontrado no conjunto do ano civil do seu próprio serial.
const isHoliday = (serial) => {
  const { year } = serialToCivil(serial);
  return holidaySetOfYear(year).has(serial);
};

export const isBusinessDay = (serial) => !isWeekend(serial) && !isHoliday(serial);

/** O último dia calendário de `competence` ("YYYY-MM"), como dia serial (design D4). */
export const referenceDayOf = (competence) => {
  const { year, month } = parseMonthKey(competence);
  return civilToSerial(year, month, daysInMonth(year, month));
};

/**
 * Move `serial` por `count` dias úteis. `count === 0` devolve `serial` sem alteração,
 * seja ou não dia útil (cenário "Defasagem zero em dias úteis"). Para `count !== 0`, o dia
 * de partida nunca é contado — então uma partida não útil (cenário "Data de referência não
 * útil") resolve naturalmente contando a partir do próximo dia útil, sem caso especial.
 */
export const advanceBusinessDays = (serial, count) => {
  if (!Number.isSafeInteger(serial)) throw new RangeError(`serial must be a safe integer, got: ${serial}`);
  if (!Number.isSafeInteger(count)) throw new RangeError(`count must be a safe integer, got: ${count}`);
  if (count === 0) return serial;
  const step = count > 0 ? 1 : -1;
  let remaining = Math.abs(count);
  let current = serial;
  while (remaining > 0) {
    current += step;
    if (isBusinessDay(current)) remaining -= 1;
  }
  return current;
};
